import hybridApiClient from './hybridApiClient';
import { SparePart } from '../types/typesSpareParts';

function uniqueCategoryStrings(items: string[]): string[] {
  return Array.from(new Set(items.map((x) => String(x || '').trim()).filter(Boolean)));
}

function parseCategoriesFromApi(value: unknown): string[] {
  if (Array.isArray(value)) {
    return uniqueCategoryStrings(value.map((x) => String(x || '')));
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return uniqueCategoryStrings(parsed.map((x) => String(x || '')));
      }
    } catch {
      return [value.trim()];
    }
  }
  return [];
}

function normalizeSparePartFromApi(raw: unknown): SparePart {
  const row = (raw && typeof raw === 'object' ? raw : {}) as SparePart;
  return {
    ...row,
    categories: parseCategoriesFromApi((raw as { categories?: unknown })?.categories),
  };
}

export async function fetchSparePartsByDeviceFromHybrid(
  deviceId: string,
): Promise<{ total: number; data: SparePart[] }> {
  try {
    const response = await hybridApiClient.get('/api/spare-parts', {
      params: { deviceId, limit: 500, offset: 0 },
    });
    const payload = response.data as { total: number; data: unknown[] };
    return {
      total: payload.total || 0,
      data: (payload.data || []).map((row) => normalizeSparePartFromApi(row)),
    };
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 404 || error?.message === 'Network Error') {
      return { total: 0, data: [] };
    }
    throw error;
  }
}

export async function fetchSparePartByIdFromHybrid(partId: string): Promise<SparePart | null> {
  const id = String(partId || '').trim();
  if (!id) return null;
  try {
    const response = await hybridApiClient.get(`/api/spare-parts/${id}`);
    return response.data ? normalizeSparePartFromApi(response.data) : null;
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 404) return null;
    if (status === 401 || error?.message === 'Network Error') {
      return null;
    }
    throw error;
  }
}

export type LowStockAlertRow = {
  id: string;
  deviceId?: string;
  description?: string;
  sku?: string;
  inventory?: number;
  lowInventoryThreshold?: number;
  updatedAt?: string;
  createdAt?: string;
};

const LOW_STOCK_API_MODE_KEY = 'spare-parts:low-stock-api:v1';

type LowStockApiMode = 'unknown' | 'available' | 'legacy';

function readLowStockApiMode(): LowStockApiMode {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return 'unknown';
    const v = window.sessionStorage.getItem(LOW_STOCK_API_MODE_KEY);
    if (v === 'available' || v === 'legacy') return v;
  } catch {
    /* ignore */
  }
  return 'unknown';
}

function writeLowStockApiMode(mode: 'available' | 'legacy'): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(LOW_STOCK_API_MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

/** אחרי deploy לשרת — מאפשר ניסיון חוזר ל-endpoint הקל */
export function resetLowStockAlertsApiModeCache(): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(LOW_STOCK_API_MODE_KEY);
    }
  } catch {
    /* ignore */
  }
}

async function fetchLowStockAlertsLegacy(limit: number): Promise<LowStockAlertRow[]> {
  const parts = await fetchAllSparePartsFromHybrid();
  return parts
    .filter((part) => {
      const threshold = Number(part.lowInventoryThreshold || 0);
      const inventory = Number(part.inventory || 0);
      return threshold > 0 && inventory <= threshold;
    })
    .slice(0, limit)
    .map((part) => ({
      id: String(part.id || ''),
      deviceId: part.deviceId,
      description: part.description,
      sku: part.sku,
      inventory: part.inventory,
      lowInventoryThreshold: part.lowInventoryThreshold,
      updatedAt: String((part as { updatedAt?: string }).updatedAt || ''),
      createdAt: String((part as { createdAt?: string }).createdAt || ''),
    }));
}

let lowStockInFlight: Promise<LowStockAlertRow[]> | null = null;

export async function fetchLowStockAlertsFromHybrid(
  limit = 50,
): Promise<LowStockAlertRow[]> {
  const cap = Math.min(Math.max(limit, 1), 200);
  const mode = readLowStockApiMode();

  if (mode === 'legacy') {
    return fetchLowStockAlertsLegacy(cap);
  }

  // מניעת קריאות כפולות-מקבילות לפני שהמצב נכתב ל-sessionStorage
  if (lowStockInFlight) return lowStockInFlight;

  lowStockInFlight = (async (): Promise<LowStockAlertRow[]> => {
    try {
      const response: any = await hybridApiClient.get('/api/spare-parts/low-stock-alerts', {
        params: { limit: cap },
      });
      writeLowStockApiMode('available');
      return Array.isArray(response.data?.data) ? response.data.data : [];
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 404) {
        writeLowStockApiMode('legacy');
        return fetchLowStockAlertsLegacy(cap);
      }
      throw error;
    } finally {
      lowStockInFlight = null;
    }
  })();
  return lowStockInFlight;
}

export async function fetchAllSparePartsFromHybrid(): Promise<SparePart[]> {
  try {
    const response: any = await hybridApiClient.get('/api/spare-parts', {
      params: { limit: 5000, offset: 0 },
    });
    return (response.data?.data || []).map((row: unknown) => normalizeSparePartFromApi(row));
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 404 || error?.message === 'Network Error') {
      return [];
    }
    throw error;
  }
}

export async function createSparePartInHybrid(sparePart: SparePart): Promise<SparePart> {
  const response = await hybridApiClient.post('/api/spare-parts', {
    ...sparePart,
    categories: Array.isArray(sparePart.categories) ? sparePart.categories : [],
  });
  return normalizeSparePartFromApi(response.data);
}

export async function updateSparePartInHybrid(sparePart: SparePart): Promise<SparePart> {
  const response = await hybridApiClient.patch(`/api/spare-parts/${sparePart.id}`, {
    ...sparePart,
    categories: Array.isArray(sparePart.categories) ? sparePart.categories : [],
  });
  return normalizeSparePartFromApi(response.data);
}

/** עדכון מלאי בלבד — לא שולח categories כדי שלא יידרסו בשרת */
export async function updateSparePartInventoryInHybrid(
  partId: string,
  inventory: number,
): Promise<SparePart> {
  const response = await hybridApiClient.patch(`/api/spare-parts/${partId}`, { inventory });
  return normalizeSparePartFromApi(response.data);
}

export async function deleteSparePartInHybrid(sparePartId: string): Promise<void> {
  await hybridApiClient.delete(`/api/spare-parts/${sparePartId}`);
}
